<template>
  <div id="appraisal-process-content" data-cy="appraisal-process-content" class="card-item">
    <div class="appraisal-process-container">
      <input
        id="appraisal-process-fileUpload"
        ref="file"
        data-cy="appraisal-process-fileUpload"
        type="file"
        hidden
        @change="getAppraisalFile()">
      <button
        id="appraisal-upload-button"
        data-cy="appraisal-upload-button"
        :disabled="!checkUserFeature('element.trading.button.cargar')"
        class="button is-button-second is-rounded"
        @click="showPopUpFile()">
        <span class="icon is-small">
          <i class="fas fa-upload" />
        </span>
        <span>Cargar Valorización</span>
      </button>
    </div>
    <div v-if="appraisalFileCharged && !isErrorMessage" data-cy="appraisal-inputs" class="appraisal-inputs columns is-centered">
      <div class="column is-one-third">
        <b-field label="Comprador" custom-class="has-text-weight-normal has-text-align-center">
          <b-select
            id="appraisal-investor"
            v-model="newInvestor"
            data-cy="appraisal-investor"
            expanded
            size="is-small"
            rounded>
            <option :value="defaultInvestorSelected">Seleccione</option>
            <option v-for="investor in investors" :key="investor._id" :value="investor">
              {{ investor.name }}
            </option>
          </b-select>
        </b-field>
        <b-field label="Tipo" custom-class="has-text-weight-normal">
          <b-select
            id="appraisal-sale-type"
            v-model="salesType"
            data-cy="appraisal-sale-type"
            expanded
            size="is-small"
            rounded>
            <option :value="defaultSalesTypeSelected">Seleccione</option>
            <option v-for="type in appraisalSalesTypes" :key="type.name" :value="type.name">
              {{ type.name }}
            </option>
          </b-select>
        </b-field>
        <b-field label="Fecha Venta" custom-class="has-text-weight-normal">
          <b-input
            id="appraisal-sale-date"
            v-model="salesDate"
            rounded
            size="5 is-small"
            data-cy="appraisal-sale-date" />
        </b-field>
        <b-field class="pt-3">
          <b-button
            id="appraisal-process-button"
            expanded
            data-cy="appraisal-process-button"
            class="is-button-primary"
            :disabled="isProcessingDisabled"
            rounded
            icon-left="fas fa-cog"
            @click="showConfirmModal()">
            Procesar Valorización
          </b-button>
        </b-field>
      </div>
    </div>
    <spinner :is-loading="isLoading" />
    <div
      v-if="isErrorMessage || isWrongDate || isErrorProcess"
      id="appraisal-process-error-title"
      data-cy="appraisal-process-error-title"
      class="appraisal-process-info has-text-centered error-message">
      <span>
        {{ isWrongDate || errorMessage }}
      </span>
    </div>
    <AppraisalProcessConfirmModal
      :appraisal="appraisal"
      :is-component-modal-active="isComponentModalActive"
      @process="process"
      @cancelProcess="cancelProcess" />
    <div v-if="appraisalResult">
      <appraisal-item
        :appraisal="appraisalResult"
        :pos="0" />
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { getInvestors } from '@/application/services/api-clients/investors';
import { createAppraisal, getAppraisalOperations } from '@/application/services/api-clients/appraisal';
import Appraisal from '@/domain/dto/Appraisal';
import appraisalSalesTypes from '@/domain/constants/appraisal-sales-types';
import { appraisalInformationFromFilename } from '@/application/services/readers/appraisal-info';
import AppraisalItem from '@/ui/views/internal/trading/AppraisalItem.vue';
import { get } from '@/infrastructure/lodash';
import userFeatures from '@/ui/mixins/user-features';
import Spinner from '../../../components/Spinner.vue';
import AppraisalProcessConfirmModal from './AppraisalProcessConfirmModal.vue';

const ERROR_MSG_OPERATIONS_DIFFERENT = 'Number of operations found is different to number of operations queried';
const ERROR_MSG_OPERATIONS_EMPTY = 'Appraisals operations must not be empty';
const ERROR_MSG_DEFAULT = 'Error de conexión. Por favor inténtalo nuevamente.';

export default {
  components: {
    AppraisalItem,
    Spinner,
    AppraisalProcessConfirmModal
  },
  data() {
    return {
      investors: [],
      isLoading: false,
      isErrorMessage: false,
      isErrorProcess: false,
      file: File,
      defaultInvestorSelected: undefined,
      defaultSalesTypeSelected: undefined,
      appraisalFileCharged: false,
      appraisal: new Appraisal(),
      appraisalSalesTypes,
      salesDate: undefined,
      salesType: undefined,
      newInvestor: {},
      isComponentModalActive: false,
      appraisalResult: '',
      errorMessage: ERROR_MSG_DEFAULT,
      appraisalProcessed: false
    };
  },
  computed: {
    // ...mapGetters({ investors: 'endorsementModule/investors' }),
    isWrongDate() {
      let message;
      if (this.salesDate && !this.validateDate(this.salesDate)) message = 'Error, verifica la fecha ingresada.';
      else message = '';
      return message;
    },
    isProcessingDisabled() {
      return (this.appraisalProcessed === true || this.newInvestor === undefined || this.salesDate === '' || this.isWrongDate || this.salesType === undefined);
    }
  },
  async mounted() {
    this.setInvestors();
  },
  methods: {
    async setInvestors() {
      const investors = await getInvestors();
      const investorsNames = [];
      let investorsResult = JSON.parse(JSON.stringify(investors));
      investors.forEach(investor => {
        if (investorsNames.includes(investor.name)) {
          investorsResult = investorsResult.filter(inv => inv.name !== investor.name);
        }
        investorsNames.push(investor.name);
      });
      this.investors = investorsResult;
      return this.investors;
    },
    // Por algún motivo el mixins no funciona en este modulo.
    checkUserFeature(name) {
      return userFeatures.methods.checkUserFeature(name, this.$store);
    },
    // ...mapActions({ setInvestors: 'endorsementModule/setInvestors' }),
    async getAppraisalFile() {
      this.setFile();
      try {
        this.appraisalResult = undefined;
        this.isErrorMessage = false;
        this.appraisalFileCharged = true;
        this.appraisal = await getAppraisalOperations(this.file);
        const appraisalInfo = appraisalInformationFromFilename(this.file.name, this.investors);
        const investorFromAppraisalInfo = this.getInvestorByName(appraisalInfo.investor);
        this.appraisal.setInvestorName(investorFromAppraisalInfo.name);
        this.appraisal.setInvestorId(investorFromAppraisalInfo._id);
        this.appraisal.setSalesType(appraisalInfo.type);
        this.appraisal.setSalesDate(appraisalInfo.date || this.processCurrentDate(this.getCurrentDate()));
        this.salesDate = this.appraisal.salesDate;
        this.salesType = this.appraisal.salesType;
        this.newInvestor._id = this.appraisal.investorId;
        this.newInvestor.name = this.appraisal.investorName;
        this.appraisal.setTotalOperations(parseInt(this.appraisal.appraisalsOperations.length, 10));
        // TODO: leer status del archivo, en vez de setear este valor por defecto
        this.appraisal.tradingStatus = 'COMPLETED';
      } catch (e) {
        this.isErrorMessage = true;
        this.appraisalFileCharged = false;

        this.errorMessage = e.message.includes('invalid') ? `Revisa el nombre de las columnas del archivo (${e.message}).` : this.errorMessage;
        // this.result = [];
      }
      this.appraisalProcessed = false;
      this.$refs.file.value = '';
    },
    async showPopUpFile() {
      document.getElementById('appraisal-process-fileUpload')
        .click();
    },
    setFile() {
      [this.file] = this.$refs.file.files;
    },
    getCurrentDate() {
      return new Date();
    },
    getInvestorByName(name) {
      const result = this.investors.filter(investor => investor.name === name);
      return result.length === 1 ? result[0] : result;
    },
    processCurrentDate() {
      const padWithZero = number => (number < 10 ? `0${number}` : `${number}`);
      const today = this.getCurrentDate();
      return `${today.getFullYear()}-${padWithZero(today.getMonth() + 1)}-${padWithZero(today.getDate())}`;
    },
    validateDate(dateString = '') {
      const regEx = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateString.match(regEx)) {
        return false;
      }
      const d = new Date(dateString);
      const dNum = d.getTime();
      if (!dNum && dNum !== 0) {
        return false;
      }
      if (d.toISOString().slice(0, 10) !== dateString) {
        return false;
      }
      return true;
    },
    showConfirmModal() {
      this.appraisal.salesDate = this.salesDate;
      this.appraisal.salesType = this.salesType;
      this.appraisal.investorName = this.newInvestor.name;
      this.appraisal.investorId = this.newInvestor._id;
      this.isComponentModalActive = true;
    },
    cancelProcess() {
      this.isComponentModalActive = false;
    },
    async process() {
      this.isComponentModalActive = false;
      this.isErrorProcess = false;
      this.isLoading = true;
      try {
        this.appraisalResult = await createAppraisal(this.appraisal);
        this.appraisalProcessed = true;
        this.isLoading = false;
      } catch (e) {
        const errorMessage = get(e, 'response.data.message');
        if (errorMessage === ERROR_MSG_OPERATIONS_DIFFERENT) {
          this.errorMessage = 'El número de operaciones en los registros del producto es distinto a los ingresados en el archivo';
        } else if (errorMessage === ERROR_MSG_OPERATIONS_EMPTY) {
          this.errorMessage = 'El archivo excel no cuenta con operaciones';
        } else {
          this.errorMessage = ERROR_MSG_DEFAULT;
        }
        this.isLoading = false;
        this.appraisalProcessed = false;
        this.isErrorProcess = true;
        this.appraisalResult = undefined;
      }
    }
  }
};
</script>

<style scoped>
.appraisal-process-container {
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: space-between;
  flex-flow: wrap;
}

.appraisal-process-info {
  grid-template-columns: 1fr 10fr;
  gap: 1rem;
  align-items: center;
  font-family: var(--secondary-font);
  color: var(--color-nickel);
}

.appraisal-process-info i {
  color: var(--primary);
}

.spinner-area {
  padding-top: 5rem;
}

.error-message {
  display: flex;
  justify-content: center;
  color: red;
}

.appraisal-inputs {
  margin-top: 1rem;
}

.appraisal-inputs-item {
  display: flex;
  flex-flow: column grid;
  justify-items: start;
  align-items: center;
  gap: 0.5rem;
  margin-top: .5rem;
}

.appraisal-inputs-item > label {
  padding-right: .5rem;
  width: 7rem;
}

#appraisal-process-button {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.input-gray #appraisal-sale-date {
  border-radius: 40px !important;
  box-shadow: none !important;
  background-color: #EFEFEF !important;
  width: auto;
  max-width: none;
}
@media (max-width: 640px) {
  .appraisal-inputs-item {
    display: grid;
  }
}
</style>
